import { FIRST_SCENE_SLUG } from "./firstScene";
import {
  catalogSeedId,
  type CatalogCategoryId,
  type CatalogLevel,
  type CatalogSituation,
} from "./catalog";
import {
  asConfidenceScore,
  parseSessionAnalysis,
  TOO_SHORT_SUMMARY,
  type SessionAnalysis,
} from "./sessionAnalysis";
import { sceneBrief, sceneHint, UNCERTAIN_COACH } from "./sceneCast";
import { isInterpreterAnalysis } from "./interpreter";

export type MissionStatus = "me-viro" | "quase" | "ainda-nao";

export type FeaturedMissionId =
  | "aeroporto"
  | "restaurante"
  | "reuniao"
  | "hotel"
  | "medico"
  | "uber";

export type FeaturedMission = {
  id: FeaturedMissionId;
  name: string;
  seedId: string;
  category: CatalogCategoryId;
  rolePt: string;
  greeting: string;
  /** Título de lugar no Início, ex. "No aeroporto". */
  placeTitle: string;
  defaultLevel: CatalogLevel;
};

export const FEATURED_MISSIONS: FeaturedMission[] = [
  {
    id: "aeroporto",
    name: "Aeroporto",
    seedId: "checkin-aeroporto",
    category: "viagem",
    rolePt: "Você é o passageiro",
    greeting: "Amanhã é o embarque.",
    placeTitle: "No aeroporto",
    defaultLevel: "beginner",
  },
  {
    id: "restaurante",
    name: "Restaurante",
    seedId: "restaurante-mesa",
    category: "comida",
    rolePt: "Você é o cliente",
    greeting: "Hoje é a mesa.",
    placeTitle: "No restaurante",
    defaultLevel: "beginner",
  },
  {
    id: "reuniao",
    name: "Reunião",
    seedId: "reuniao-standup",
    category: "trabalho",
    rolePt: "Você é o colega",
    greeting: "A stand-up começa agora.",
    placeTitle: "Na reunião",
    defaultLevel: "beginner",
  },
  {
    id: "hotel",
    name: "Hotel",
    seedId: "hotel-checkin",
    category: "viagem",
    rolePt: "Você é o hóspede",
    greeting: "Você acaba de chegar.",
    placeTitle: "No hotel",
    defaultLevel: "beginner",
  },
  {
    id: "medico",
    name: "Médico",
    seedId: "consulta-medico",
    category: "saude",
    rolePt: "Você é o paciente",
    greeting: "A consulta é agora.",
    placeTitle: "No médico",
    defaultLevel: "beginner",
  },
  {
    id: "uber",
    name: "Uber",
    seedId: "uber",
    category: "transporte",
    rolePt: "Você é o passageiro",
    greeting: "O carro está a dois minutos.",
    placeTitle: "No Uber",
    defaultLevel: "beginner",
  },
];

const LIFE_AXIS: Array<{ id: string; label: string; missionIds: FeaturedMissionId[] }> = [
  { id: "viajar", label: "Viajar", missionIds: ["aeroporto", "hotel", "uber"] },
  { id: "comer", label: "Comer", missionIds: ["restaurante"] },
  { id: "trabalho", label: "Trabalho", missionIds: ["reuniao"] },
  { id: "saude", label: "Saúde", missionIds: ["medico"] },
];

export const MISSION_LIFE_AXIS = LIFE_AXIS;

const CATEGORY_ROLE: Record<CatalogCategoryId, string> = {
  viagem: "Você é o viajante",
  comida: "Você é o cliente",
  trabalho: "Você é o profissional",
  saude: "Você é o paciente",
  compras: "Você é o cliente",
  social: "Você é você",
  casa: "Você é o morador",
  estudo: "Você é o aluno",
  transporte: "Você é o passageiro",
  emergencia: "Você precisa de ajuda",
  banco: "Você é o cliente",
  entretenimento: "Você é o visitante",
};

export function isFeaturedMissionId(value: string | null | undefined): value is FeaturedMissionId {
  return FEATURED_MISSIONS.some((mission) => mission.id === value);
}

export function findFeaturedMission(id: string | null | undefined): FeaturedMission | undefined {
  return FEATURED_MISSIONS.find((mission) => mission.id === id);
}

export function featuredMissionForSituation(
  situation: Pick<CatalogSituation, "slug" | "category" | "level">,
): FeaturedMission | undefined {
  const seedId = catalogSeedId(situation);
  return FEATURED_MISSIONS.find(
    (mission) => mission.seedId === seedId && mission.category === situation.category,
  );
}

export function missionSlug(mission: FeaturedMission, level: CatalogLevel = mission.defaultLevel): string {
  return `${mission.category}-${mission.seedId}-${level}`;
}

export function roleInPortuguese(situation: CatalogSituation): string {
  return featuredMissionForSituation(situation)?.rolePt ?? CATEGORY_ROLE[situation.category];
}

export function sceneGreeting(situation: CatalogSituation): string {
  return featuredMissionForSituation(situation)?.greeting ?? "Sua próxima cena está pronta.";
}

export function sceneObjective(situation: CatalogSituation): string {
  if (situation.slug === FIRST_SCENE_SLUG) return "sair com o cartão de embarque";
  const goal = situation.studentGoal.trim();
  if (!goal) return goal;
  return goal.charAt(0).toLowerCase() + goal.slice(1);
}

export function sceneMinutes(situation: CatalogSituation): number {
  if (situation.slug === FIRST_SCENE_SLUG) return 5;
  return situation.estimatedMinutes;
}

export type SceneDebrief = {
  handled: string;
  stuck: string;
  coachLine: string;
  repaired?: string;
  trusted: boolean;
};

export function sceneFocus(
  situation: CatalogSituation,
): Pick<SceneDebrief, "handled" | "stuck" | "coachLine"> {
  const brief = sceneBrief(situation);
  const stuck = brief.stuckAnchor;
  return {
    handled: brief.victory,
    stuck,
    coachLine: stuck.startsWith("o ") || stuck.startsWith("a ")
      ? `Vamos repetir só ${stuck}.`
      : `Vamos repetir só o ${stuck}.`,
  };
}

export function sceneDebrief(
  situation: CatalogSituation,
  analysis: SessionAnalysis | null | undefined,
): SceneDebrief {
  if (analysis?.tooShort) {
    return {
      handled: analysis.handled ?? "",
      stuck: analysis.stuck ?? "",
      coachLine: analysis.coachLine || analysis.summary,
      repaired: analysis.repaired,
      trusted: false,
    };
  }

  const focus = sceneFocus(situation);
  const stuck = analysis?.stuck?.trim() ?? "";
  const trusted = Boolean(analysis) && analysis?.uncertain !== true && stuck.length > 0;

  if (!trusted) {
    return {
      handled: analysis?.handled?.trim() || "",
      stuck: "",
      coachLine: analysis?.coachLine?.trim() || UNCERTAIN_COACH,
      repaired: analysis?.repaired,
      trusted: false,
    };
  }

  return {
    handled: analysis?.handled?.trim() || focus.handled,
    stuck,
    coachLine: analysis?.coachLine || focus.coachLine,
    repaired: analysis?.repaired,
    trusted: true,
  };
}

export function sceneStuckHint(situation: CatalogSituation): string {
  return sceneHint(situation, 0);
}

export function sceneRetryCta(stuck: string, trusted = true): string {
  if (!trusted || !stuck.trim()) return "Treinar de novo";
  if (stuck.startsWith("o ") || stuck.startsWith("a ")) return `Treinar só ${stuck}`;
  return `Treinar só o ${stuck}`;
}

export function sceneCardSummary(
  situation: CatalogSituation,
  analysis: SessionAnalysis | null | undefined,
  practiced: boolean,
  sessionStatus?: string | null,
): string | null {
  if (!practiced) return null;
  if (analysis?.tooShort || sessionStatus === "too_short") {
    return analysis?.summary || TOO_SHORT_SUMMARY;
  }
  const debrief = sceneDebrief(situation, analysis);
  if (!debrief.trusted) {
    if (debrief.handled) return `Se virou em ${debrief.handled}`;
    return analysis?.summary || UNCERTAIN_COACH;
  }
  return `Se virou em ${debrief.handled} · Travou em ${debrief.stuck}`;
}

export function statusFromConfidence(score: number | null | undefined): MissionStatus {
  if (score == null) return "ainda-nao";
  if (score >= 4) return "me-viro";
  if (score >= 1) return "quase";
  return "ainda-nao";
}

export function missionPracticePath(missionId: FeaturedMissionId): string {
  return `/app/practice?mission=${missionId}`;
}

export type MissionProgress = {
  status: MissionStatus;
  rehearsals: number;
  lastSlug?: string;
  handled?: string;
  stuck?: string;
};

export function emptyMissionProgress(): Record<FeaturedMissionId, MissionProgress> {
  return {
    aeroporto: { status: "ainda-nao", rehearsals: 0 },
    restaurante: { status: "ainda-nao", rehearsals: 0 },
    reuniao: { status: "ainda-nao", rehearsals: 0 },
    hotel: { status: "ainda-nao", rehearsals: 0 },
    medico: { status: "ainda-nao", rehearsals: 0 },
    uber: { status: "ainda-nao", rehearsals: 0 },
  };
}

export function missionProgressFromSessions(
  rows: Array<{ catalog_slug?: string | null; analysis?: unknown; status?: string | null }>,
  items: CatalogSituation[],
): Record<FeaturedMissionId, MissionProgress> {
  const progress = emptyMissionProgress();
  const findSituation = (slug: string | null | undefined) =>
    items.find((item) => item.slug === slug);

  for (const row of rows) {
    if (isInterpreterAnalysis(row.analysis)) continue;
    const situation = findSituation(row.catalog_slug ?? undefined);
    if (!situation) continue;
    const mission = featuredMissionForSituation(situation);
    if (!mission) continue;
    const current = progress[mission.id];
    current.rehearsals += 1;
    if (!current.lastSlug) current.lastSlug = situation.slug;
  }

  for (const row of rows) {
    if (isInterpreterAnalysis(row.analysis)) continue;
    const situation = findSituation(row.catalog_slug ?? undefined);
    if (!situation) continue;
    const mission = featuredMissionForSituation(situation);
    if (!mission) continue;
    const current = progress[mission.id];
    if (current.lastSlug && current.lastSlug !== situation.slug) continue;
    if (row.status === "too_short") continue;
    const parsed = parseSessionAnalysis(row.analysis);
    const raw = row.analysis && typeof row.analysis === "object"
      ? row.analysis as Record<string, unknown>
      : null;
    const confidence = parsed?.confidenceScore ?? asConfidenceScore(raw?.confidenceScore ?? raw?.confidence_score);
    const handled = parsed?.handled ?? (typeof raw?.handled === "string" ? raw.handled : undefined);
    const stuck = parsed?.stuck ?? (typeof raw?.stuck === "string" ? raw.stuck : undefined);
    if (confidence) {
      current.status = statusFromConfidence(confidence);
      current.handled = handled;
      current.stuck = stuck;
    } else if (row.status === "ready" && current.status === "ainda-nao") {
      current.status = "quase";
      current.handled = handled;
      current.stuck = stuck;
    }
  }

  return progress;
}
